import React, { useCallback, useState } from 'react';
import { ErrorApi, AlertApi } from '@backstage/core-plugin-api';
import { ExecuteActionOptions } from '../../../api';
import { MenuPosition } from '../../types';
import {
  KubernetesActionType,
  KubernetesResourceType,
  ExecuteActionResponse,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { CRDKubernetesConfig } from '@internal/backstage-plugin-compute-resource-discovery-new-common';

interface UseActionManagementProps {
  executeAction: (
    options: ExecuteActionOptions,
  ) => Promise<ExecuteActionResponse>;
  refresh: () => void;
  kubernetesConfig: CRDKubernetesConfig;
  errorApi: ErrorApi;
  alertApi: AlertApi;
  entityRef: string;
  projectCode: string;
}

export const useActionManagement = ({
  executeAction,
  refresh,
  errorApi,
  alertApi,
  kubernetesConfig,
  entityRef,
  projectCode,
}: UseActionManagementProps) => {
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    mouseX: null,
    mouseY: null,
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [kubernetesConnectionDialogOpen, setKubernetesConnectionDialogOpen] =
    useState(false);
  const [selectedAction, setSelectedAction] =
    useState<KubernetesActionType | null>(null);
  const [service, setService] = useState<KubernetesResourceType | null>(null);
  const [isActionInProgress, setActionInProgress] = useState<boolean>(false);

  // Handle opening the action menu from table-level action
  const handleTableActionClick = useCallback((event: React.MouseEvent) => {
    setMenuPosition({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuPosition({
      mouseX: null,
      mouseY: null,
    });
  }, []);

  const handleActionClick = useCallback(
    (action: KubernetesActionType, service: KubernetesResourceType) => {
      setSelectedAction(action);
      setService(service);
      setConfirmationDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose],
  );

  const handleOpenConnection = useCallback(() => {
    setKubernetesConnectionDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleConfirmAction = useCallback(async () => {
    if (!selectedAction || !service) return;

    setConfirmationDialogOpen(false);
    setActionInProgress(true);

    try {
      const response = await executeAction({
        serviceType: 'kubernetes',
        entityRef,
        kubernetes: {
          action: selectedAction,
          resourceType: service,
          kubeContext: kubernetesConfig.kubeContext,
          projectCode,
        },
      });

      if (response.status === 'success') {
        alertApi.post({
          message: `Action ${selectedAction} for ${service} has been initiated. ${response.response?.message}`,
          severity: 'success',
        });
        refresh();
      } else {
        throw new Error(response.error || 'Unknown error');
      }
    } catch (error) {
      errorApi.post(
        new Error(`Error to execute action ${selectedAction} for ${service}`),
      );
    } finally {
      setActionInProgress(false);
      setSelectedAction(null);
      setService(null);
    }
  }, [
    selectedAction,
    executeAction,
    refresh,
    kubernetesConfig,
    projectCode,
    entityRef,
  ]);

  return {
    menuPosition,
    confirmationDialogOpen,
    kubernetesConnectionDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    handleOpenConnection,
    setConfirmationDialogOpen,
    setKubernetesConnectionDialogOpen,
  };
};
